/** Shared strip labels (Journey, From, To, Departure, Travelers, Class). */
export const FLIGHT_SEARCH_STRIP_LABEL_FONT_SIZE = '14px';
export const FLIGHT_SEARCH_STRIP_LABEL_FONT_WEIGHT = 500;
export const FLIGHT_SEARCH_STRIP_LABEL_LINE_HEIGHT = '18px';
export const FLIGHT_SEARCH_STRIP_LABEL_LETTER_SPACING = '0.15px';
export const FLIGHT_SEARCH_STRIP_LABEL_CHEVRON_PX = 12;

export function getFlightSearchStripLabelSx() {
  return {
    color: 'var(--Neutrals-base-black, #20202A)',
    fontFamily: 'Host Grotesk',
    fontSize: FLIGHT_SEARCH_STRIP_LABEL_FONT_SIZE,
    fontStyle: 'normal',
    fontWeight: FLIGHT_SEARCH_STRIP_LABEL_FONT_WEIGHT,
    lineHeight: FLIGHT_SEARCH_STRIP_LABEL_LINE_HEIGHT,
    letterSpacing: FLIGHT_SEARCH_STRIP_LABEL_LETTER_SPACING,
  };
}

export function getFlightSearchStripLabelRowSx() {
  return {
    display: 'flex',
    alignItems: 'center',
    height: FLIGHT_SEARCH_STRIP_LABEL_LINE_HEIGHT,
    minHeight: FLIGHT_SEARCH_STRIP_LABEL_LINE_HEIGHT,
    margin: 0,
    padding: 0,
    width: '100%',
    boxSizing: 'border-box',
  };
}

/** Shared strip values — 16px, not a viewport clamp that grows on wide screens. */
export const FLIGHT_SEARCH_STRIP_VALUE_FONT_SIZE = '1rem';
export const FLIGHT_SEARCH_STRIP_VALUE_LINE_HEIGHT = '28px';
export const FLIGHT_SEARCH_STRIP_VALUE_FONT_WEIGHT = 700;

export function getFlightSearchStripValueRowSx() {
  return {
    height: FLIGHT_SEARCH_STRIP_VALUE_LINE_HEIGHT,
    minHeight: FLIGHT_SEARCH_STRIP_VALUE_LINE_HEIGHT,
    lineHeight: FLIGHT_SEARCH_STRIP_VALUE_LINE_HEIGHT,
    margin: 0,
    padding: 0,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
  };
}

/** Fits `Sep 30, 2026` at the strip's 16px bold value size. */
export const FLIGHT_SEARCH_DATE_MIN_WIDTH = '112px';

/** "Select Origin" at 16px Host Grotesk medium, plus a small buffer. */
export const FLIGHT_SEARCH_FROM_FIELD_WIDTH = '108px';

/** "Select Destination" at 16px Host Grotesk medium, plus a small buffer. */
export const FLIGHT_SEARCH_TO_FIELD_WIDTH = '144px';

/** "1 Adult, 0 Children" at 16px Host Grotesk medium. */
export const FLIGHT_SEARCH_TRAVELERS_MIN_WIDTH = '152px';

export const FLIGHT_SEARCH_STRIP_COLUMN_GAP = '16px';
/** Wrapped fields sit on the next line with no extra band above the button. */
export const FLIGHT_SEARCH_STRIP_ROW_GAP = '0px';

/** Class column plus the Let's Go button. */
export const FLIGHT_SEARCH_WRAPPED_TAIL_WIDTH_PX = 184;

/** Inset of the search pill on the right, below the wide desktop row. */
export const FLIGHT_SEARCH_STRIP_EDGE_PADDING_PX = 12;

/**
 * Keeps From / To / dates / travelers clear of Class + Let's Go, which sit in
 * the top-right corner below the wide desktop row.
 */
export const FLIGHT_SEARCH_STRIP_PINNED_TAIL_PADDING = `${
  FLIGHT_SEARCH_STRIP_EDGE_PADDING_PX
  + Number.parseInt(FLIGHT_SEARCH_STRIP_COLUMN_GAP, 10)
  + FLIGHT_SEARCH_WRAPPED_TAIL_WIDTH_PX
}px`;

/** Right padding for the homepage strip. The hero strip keeps its own padding. */
export function getFlightSearchStripPaddingRight(hideTripTypeSelector, isEmbedded) {
  if (hideTripTypeSelector && isEmbedded) return 0;
  return {
    xs: 0,
    sm: 0,
    md: FLIGHT_SEARCH_STRIP_PINNED_TAIL_PADDING,
    lg: `${FLIGHT_SEARCH_STRIP_EDGE_PADDING_PX}px`,
  };
}

/** One-way travel date is never ellipsized. At lg the column grows with the desktop strip. */
export function getFlightSearchDateFieldLayout() {
  return {
    flex: { xs: '1 1 100%', sm: '1 1 100%', md: '0 0 auto', lg: '1 1 0' },
    flexShrink: 0,
    minWidth: {
      xs: '100%',
      sm: '100%',
      md: FLIGHT_SEARCH_DATE_MIN_WIDTH,
      lg: FLIGHT_SEARCH_DATE_MIN_WIDTH,
    },
  };
}

/**
 * Below lg the city column stays as wide as its placeholder.
 * At lg it grows with the desktop strip and does not crop that placeholder.
 */
export function getFlightSearchAirportFieldLayout(width) {
  return {
    flex: { xs: '1 1 0', sm: '1 1 0', md: '0 0 auto', lg: '1.1 1 0' },
    width: { md: width, lg: 'auto' },
    minWidth: { xs: 0, sm: 0, md: width, lg: width },
    maxWidth: { md: width, lg: 'none' },
  };
}

/**
 * Class and Let's Go travel as one piece on the homepage. Below lg that piece
 * is pinned to the top-right corner of the pill. At lg each field joins the wide row.
 */
export function getFlightSearchWrappedTailLayout(grouped = true) {
  if (!grouped) {
    return { display: 'contents' };
  }
  const edge = `${FLIGHT_SEARCH_STRIP_EDGE_PADDING_PX}px`;
  return {
    display: { xs: 'contents', md: 'flex', lg: 'contents' },
    alignItems: { md: 'flex-start' },
    flex: { md: '0 0 auto' },
    position: { md: 'absolute', lg: 'static' },
    top: { md: edge },
    right: { md: edge },
    zIndex: { md: 1 },
  };
}

/**
 * The travellers menu is centered on this box, not the stretched desktop column,
 * so a wide screen opens it in the same place as a laptop.
 */
export function getFlightSearchTravelersAnchorSx() {
  return {
    position: 'relative',
    width: FLIGHT_SEARCH_TRAVELERS_MIN_WIDTH,
    maxWidth: '100%',
    margin: 0,
    padding: 0,
  };
}

/** Travelers keep the full count. At lg the column grows with the desktop strip. */
export function getFlightSearchTravelersFieldLayout() {
  return {
    flex: { xs: '1 1 100%', sm: '1 1 100%', md: '0 0 auto', lg: '1 1 0' },
    minWidth: {
      xs: '100%',
      sm: '100%',
      md: FLIGHT_SEARCH_TRAVELERS_MIN_WIDTH,
      lg: FLIGHT_SEARCH_TRAVELERS_MIN_WIDTH,
    },
    width: { md: FLIGHT_SEARCH_TRAVELERS_MIN_WIDTH, lg: 'auto' },
    maxWidth: { md: FLIGHT_SEARCH_TRAVELERS_MIN_WIDTH, lg: 'none' },
  };
}

export function getFlightSearchDateInputSx({ hasDate }) {
  return {
    color: hasDate ? 'var(--Neutrals-base-black, #20202A)' : 'var(--Neutrals-grey-700, #7E808F)',
    fontFamily: 'Host Grotesk',
    fontSize: FLIGHT_SEARCH_STRIP_VALUE_FONT_SIZE,
    fontWeight: hasDate ? 700 : 500,
    lineHeight: '22px',
    cursor: 'pointer',
    paddingLeft: 0,
    caretColor: 'transparent !important',
    whiteSpace: 'nowrap',
    overflow: 'visible',
    textOverflow: 'clip',
    width: FLIGHT_SEARCH_DATE_MIN_WIDTH,
    minWidth: FLIGHT_SEARCH_DATE_MIN_WIDTH,
  };
}
